<template>
  <div>
    <DataTable :columns="columnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="11" :url="dataURL" :filterOptions="filterOptions" :displayTools="true" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Purchase Orders" :useQueryData="useQueryData">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkPurchaseOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" :newTab="openNewTab" />
          </td>
          <td>
            {{row.data.contactName}}
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-type-${row.data.type}`">{{row.data.type | formatLookupName(PurchaseOrderTypeLookup)}}</LabelBadge>
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-status-${row.data.status}`">{{row.data.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
            <LabelBadge class="label-order-tasks-assigned ml-1" v-if="row.data.isActiveTasksAssigned"><i class="far fa-fw fa-calendar-check"></i> Tasks Assigned</LabelBadge>
          </td>
          <td>
            {{row.data.supplierConfirmRef | formatHyphenEmptyValue}}
          </td>
          <td>
            {{row.data.supplierShipContainerRef | formatHyphenEmptyValue}}
          </td>
          <td>{{row.data.dateAdvisedShipping | formatDate | formatHyphenEmptyValue}}</td>
          <td>{{row.data.dateAdvised | formatDate | formatHyphenEmptyValue}}</td>
          <td>{{row.data.dateReceived | formatDate | formatHyphenEmptyValue}}</td>
          <td>{{row.data.dateRequired | formatDate | formatHyphenEmptyValue}}</td>
          <td>{{row.data.dateClosed | formatDate}}</td>
          <td>{{row.data.createdAt | formatDateTime}}</td>
          <td>
            <FormButton class="btn-default btn-xxs" @click="viewPurchaseOrderSummary($event, row.data.id)"><i class="far fa-info-circle" /></FormButton>
          </td>
        </tr>
      </template>
    </DataTable>

    <PurchaseOrderSummaryPopover ref="poSummaryPopover" />
  </div>
</template>

<script>
import Utils from '@/utils/utils'

import PurchaseOrderSummaryPopover from '@/modules/purchasing/components/purchaseOrderSummaryPopover'
import { PurchaseOrderTypes, PurchaseOrderTypeLookup, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup } from '@/store/types'

export default {
  name: "PurchaseOrderDataTable",
  components: {
    PurchaseOrderSummaryPopover
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    useQueryData: Boolean,
    filterContactId: Number,
    filterType: String
  },
  created() {
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {}
      }
      if(this.filterContactId)
        params.filter.contactId = this.filterContactId

      return '/purchasing/purchaseorders?' + $.param(params)
    },
  },
  data() {
    return {
      filterOptions: [
        {
          field: 'status',
          name: 'Status',
          options: [
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingApproval), value: PurchaseOrderStatusTypes.pendingApproval, class: 'label-po-status-pendingApproval' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingConfirmation), value: PurchaseOrderStatusTypes.pendingConfirmation, class: 'label-po-status-pendingConfirmation' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingShipping), value: PurchaseOrderStatusTypes.pendingShipping, class: 'label-po-status-pendingShipping' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingReceival), value: PurchaseOrderStatusTypes.pendingReceival, class: 'label-po-status-pendingReceival' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingAvailable), value: PurchaseOrderStatusTypes.pendingAvailable, class: 'label-po-status-pendingAvailable' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.receiving), value: PurchaseOrderStatusTypes.receiving, class: 'label-po-status-receiving' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingPickup), value: PurchaseOrderStatusTypes.pendingPickup, class: 'label-po-status-pendingPickup' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.completed), value: PurchaseOrderStatusTypes.completed, class: 'label-po-status-completed' },
            { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.cancelled), value: PurchaseOrderStatusTypes.cancelled, class: 'label-po-status-cancelled' }
          ]
        },
        {
          field: 'type',
          name: 'Type',
          columnIndex: 1,
          options: [
            { name: 'Bulk', value: PurchaseOrderTypes.bulk, class: 'label-po-type-bulk' },
            { name: 'Standard', value: PurchaseOrderTypes.standard, class: 'label-po-type-standard' },
            { name: 'External Order', value: PurchaseOrderTypes.externalOrder, class: 'label-po-type-externalOrder' },
          ]
        },
        {
          field: 'isActiveTasksAssigned',
          name: 'Tasks Assigned',
          columnIndex: 1,
          options: [
            { name: 'Tasks Assigned', value: '1', class: 'label-order-tasks-assigned' }
          ]
        }
      ],
      breadcrumbs: [
        { title: 'Purchase Order', link: '/purchasing/purchaseorders'}
      ],
      columnList: [
        { title: "Number", data: "sequenceNumber" },
        { title: "Contact", data: "contact.name" },
        { title: "Type", data: "type" },
        { title: "Status", data: "status" },
        { title: "Confirmation Ref", data: "supplierConfirmRef" },
        { title: "Shipping Ref", data: "supplierShipContainerRef" },
        { title: "Estimated Shipping", data: "dateAdvisedShipping", dateFilter: true, type: 'date' },
        { title: "Estimated Arrival", data: "dateAdvised", dateFilter: true, type: 'date' },
        { title: "Date Received", data: "dateReceived", dateFilter: true, type: 'date' },
        { title: "Date Required", data: "dateRequired", dateFilter: true, type: 'date' },
        { title: "Date Closed", data: "dateClosed", dateFilter: true, type: 'date' },
        { title: "Created", data: "createdAt", dateFilter: true, type: 'datetime' },
        { title: "Info", data: "", sortable: false, export: false },
      ]
    }
  },
  methods: {
    viewPurchaseOrderSummary(ev, purchaseOrderId) {
      this.$refs.poSummaryPopover.displayFromElement(ev.target, purchaseOrderId)
    }
  }
};
</script>
